import { api } from "@/lib/apiClient";

export type PartyLedgerTypeFilter = "all" | "customer" | "supplier" | "investor";

export type PartyLedgerParty = {
  id: number;
  name: string;
  type: string;
  phone?: string | null;
};

export type PartyLedgerLine = {
  id: number;
  date: string | null;
  voucher: string | null;
  partyId: number | null;
  partyName: string | null;
  productId: number | null;
  productName: string | null;
  saleId: number | null;
  particulars: string[];
  debit: number;
  credit: number;
  balance: number;
  balanceSide: "Dr" | "Cr";
  partyDebit: number;
  partyCredit: number;
  partyBalance: number;
  partyBalanceSide: "Dr" | "Cr";
};

export type PartyLedger = {
  filters: {
    type: PartyLedgerTypeFilter;
    partyId: number | null;
    productId: number | null;
    dateFrom: string | null;
    dateTo: string | null;
  };
  partyCurrency?: {
    id: number;
    code: string;
    symbol: string;
    name: string;
  } | null;
  parties: PartyLedgerParty[];
  lines: PartyLedgerLine[];
  totals: {
    openingBalance: number;
    openingBalanceSide: "Dr" | "Cr";
    debit: number;
    credit: number;
    balance: number;
    balanceSide: "Dr" | "Cr";
    partyOpeningBalance: number;
    partyOpeningBalanceSide: "Dr" | "Cr";
    partyDebit: number;
    partyCredit: number;
    partyBalance: number;
    partyBalanceSide: "Dr" | "Cr";
  };
};

type ApiPartyLedger = {
  filters?: {
    type?: string;
    party_id?: number | null;
    product_id?: number | null;
    date_from?: string | null;
    date_to?: string | null;
  };
  party_currency?: { id: number; code: string; symbol: string; name: string } | null;
  parties?: { id: number; name: string; type?: string; phone?: string | null }[];
  lines?: {
    id: number;
    date?: string | null;
    voucher?: string | null;
    party_id?: number | null;
    party_name?: string | null;
    product_id?: number | null;
    product_name?: string | null;
    sale_id?: number | null;
    particulars?: string[] | string;
    debit?: number | string;
    credit?: number | string;
    balance?: number | string;
    balance_side?: string;
    party_debit?: number | string;
    party_credit?: number | string;
    party_balance?: number | string;
    party_balance_side?: string;
  }[];
  totals?: {
    opening_balance?: number | string;
    opening_balance_side?: string;
    debit?: number | string;
    credit?: number | string;
    balance?: number | string;
    balance_side?: string;
    party_opening_balance?: number | string;
    party_opening_balance_side?: string;
    party_debit?: number | string;
    party_credit?: number | string;
    party_balance?: number | string;
    party_balance_side?: string;
  };
};

export type FetchPartyLedgerParams = {
  type?: PartyLedgerTypeFilter;
  partyId?: number | null;
  productId?: number | null;
  dateFrom?: string | null;
  dateTo?: string | null;
  currencyMode?: "base" | "party";
};

function ledgerQueryParams(params: FetchPartyLedgerParams = {}) {
  return {
    type: params.type ?? "all",
    party_id: params.partyId ?? undefined,
    product_id: params.productId ?? undefined,
    date_from: params.dateFrom || undefined,
    date_to: params.dateTo || undefined,
    currency_mode: params.currencyMode || undefined,
  };
}

async function downloadLedgerBlob(
  path: string,
  params: FetchPartyLedgerParams,
  fallbackFilename: string,
): Promise<void> {
  const response = await api.get<Blob>(path, {
    params: ledgerQueryParams(params),
    responseType: "blob",
  });

  const disposition = String(response.headers?.["content-disposition"] ?? "");
  const matched = /filename\*?=(?:UTF-8''|")?([^\";]+)/i.exec(disposition);
  const filename = matched?.[1] ? decodeURIComponent(matched[1].replace(/"/g, "")) : fallbackFilename;

  const url = URL.createObjectURL(response.data);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export async function downloadPartyLedgerPdf(params: FetchPartyLedgerParams = {}): Promise<void> {
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  await downloadLedgerBlob("/api/v1/parties/ledger/pdf", params, `party-ledger-${stamp}.pdf`);
}

export async function downloadPartyLedgerExcel(params: FetchPartyLedgerParams = {}): Promise<void> {
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  await downloadLedgerBlob("/api/v1/parties/ledger/excel", params, `party-ledger-${stamp}.csv`);
}

export async function fetchPartyLedgerProductOptions(params: {
  type?: PartyLedgerTypeFilter;
  partyId?: number | null;
  search?: string;
} = {}): Promise<
  {
    id: number;
    name: string;
    imageUrl: string | null;
    categoryName: string | null;
    brandName: string | null;
    inventoryStatus: string | null;
    purchasePrice: number | null;
    landedCost: number | null;
    purchaseCurrency: string | null;
  }[]
> {
  const response = await api.get<{
    data?: {
      id?: number | string;
      name?: string;
      image_url?: string | null;
      category_name?: string | null;
      brand_name?: string | null;
      inventory_status?: string | null;
      purchase_price?: number | string | null;
      landed_cost?: number | string | null;
      purchase_currency?: string | null;
    }[];
  }>("/api/v1/parties/ledger/products", {
    params: {
      type: params.type ?? "all",
      party_id: params.partyId ?? undefined,
      search: params.search?.trim() || undefined,
    },
  });

  const toNum = (v: unknown): number | null => {
    if (v == null || v === "") return null;
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : null;
  };

  return (response.data.data ?? [])
    .map((row) => ({
      id: Number(row.id ?? 0),
      name: String(row.name ?? "").trim() || `Product #${row.id ?? ""}`,
      imageUrl: row.image_url ? String(row.image_url) : null,
      categoryName: row.category_name ? String(row.category_name) : null,
      brandName: row.brand_name ? String(row.brand_name) : null,
      inventoryStatus: row.inventory_status ? String(row.inventory_status) : null,
      purchasePrice: toNum(row.purchase_price),
      landedCost: toNum(row.landed_cost),
      purchaseCurrency: row.purchase_currency ? String(row.purchase_currency) : null,
    }))
    .filter((row) => row.id > 0);
}

export async function fetchPartyLedger(params: FetchPartyLedgerParams = {}): Promise<PartyLedger> {
  const response = await api.get<{ data: ApiPartyLedger }>("/api/v1/parties/ledger", {
    params: ledgerQueryParams(params),
  });
  const raw = response.data.data ?? {};
  const typeRaw = (raw.filters?.type ?? params.type ?? "all").toLowerCase();
  const type: PartyLedgerTypeFilter =
    typeRaw === "customer" || typeRaw === "supplier" || typeRaw === "investor" ? typeRaw : "all";

  return {
    filters: {
      type,
      partyId: raw.filters?.party_id != null ? Number(raw.filters.party_id) : null,
      productId: raw.filters?.product_id != null ? Number(raw.filters.product_id) : null,
      dateFrom: raw.filters?.date_from ?? null,
      dateTo: raw.filters?.date_to ?? null,
    },
    partyCurrency: raw.party_currency
      ? {
          id: Number(raw.party_currency.id),
          code: String(raw.party_currency.code),
          symbol: String(raw.party_currency.symbol),
          name: String(raw.party_currency.name),
        }
      : null,
    parties: (raw.parties ?? []).map((p) => ({
      id: Number(p.id),
      name: p.name,
      type: p.type ?? "",
      phone: p.phone ?? null,
    })),
    lines: (raw.lines ?? []).map((line) => {
      const particularsRaw = line.particulars;
      const particulars = Array.isArray(particularsRaw)
        ? particularsRaw.map((p) => String(p).trim()).filter(Boolean)
        : String(particularsRaw ?? "")
            .split(/\s*·\s*/)
            .map((p) => p.trim())
            .filter(Boolean);

      return {
        id: Number(line.id),
        date: line.date ?? null,
        voucher: line.voucher ?? null,
        partyId: line.party_id != null ? Number(line.party_id) : null,
        partyName: line.party_name ?? null,
        productId: line.product_id != null ? Number(line.product_id) : null,
        productName: line.product_name ?? null,
        saleId: line.sale_id != null && Number(line.sale_id) > 0 ? Number(line.sale_id) : null,
        particulars,
        debit: Number(line.debit ?? 0),
        credit: Number(line.credit ?? 0),
        balance: Number(line.balance ?? 0),
        balanceSide: line.balance_side === "Cr" ? "Cr" : "Dr",
        partyDebit: Number(line.party_debit ?? 0),
        partyCredit: Number(line.party_credit ?? 0),
        partyBalance: Number(line.party_balance ?? 0),
        partyBalanceSide: line.party_balance_side === "Cr" ? "Cr" : "Dr",
      };
    }),
    totals: {
      openingBalance: Number(raw.totals?.opening_balance ?? 0),
      openingBalanceSide: raw.totals?.opening_balance_side === "Cr" ? "Cr" : "Dr",
      debit: Number(raw.totals?.debit ?? 0),
      credit: Number(raw.totals?.credit ?? 0),
      balance: Number(raw.totals?.balance ?? 0),
      balanceSide: raw.totals?.balance_side === "Cr" ? "Cr" : "Dr",
      partyOpeningBalance: Number(raw.totals?.party_opening_balance ?? 0),
      partyOpeningBalanceSide: raw.totals?.party_opening_balance_side === "Cr" ? "Cr" : "Dr",
      partyDebit: Number(raw.totals?.party_debit ?? 0),
      partyCredit: Number(raw.totals?.party_credit ?? 0),
      partyBalance: Number(raw.totals?.party_balance ?? 0),
      partyBalanceSide: raw.totals?.party_balance_side === "Cr" ? "Cr" : "Dr",
    },
  };
}
